// import { styled } from "@pigment-css/react"; // re-enable with the CTA button below
import type { Offer as OfferSchema } from "@acto/genui-components-schema";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { formatDayMonthYear } from "@acto/taskpane/field-sales-2/utils/format";
import { asCurrencyCode } from "@acto/taskpane/core/utils/currencyCode";
import { formatCurrencyValue } from "@acto/taskpane/core/utils/currencyPattern";
import {
  Card,
  Footer,
  FooterCol,
  FooterValue,
  Header,
  CardHeadline,
  MetaLabel,
} from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";

const formatOfferValue = (amount: number, currency: string, locale: string): string =>
  formatCurrencyValue(amount, locale, asCurrencyCode(currency), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/* Temporarily disabled — see the commented-out CTA in the render below.
const CtaButton = styled("button")(({ theme }) => ({
  width: "100%",
  height: theme.fieldSalesTwo.spacing[50],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: `0 ${theme.fieldSalesTwo.spacing[20]}`,
  borderRadius: theme.fieldSalesTwo.radius.circular,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  background: theme.fieldSalesTwo.colors.surface.glassStrong,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["15"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  color: theme.fieldSalesTwo.colors.neutral[900],
  cursor: "pointer",
  "&:disabled": {
    cursor: "not-allowed",
    opacity: 0.5,
  },
}));
*/

interface OfferProps {
  part: OfferSchema;
  onOpen?: (externalReference: string) => void;
}

export function Offer({ part }: OfferProps) {
  const { locale } = useLocale();
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.offer");

  const label = part.kind === "value" ? t("valueLabel") : part.category;
  const headline = part.kind === "value" ? formatOfferValue(part.value, part.currency, locale) : part.headline;

  return (
    <Card>
      <Header>
        <MetaLabel>{label}</MetaLabel>
        <CardHeadline>{headline}</CardHeadline>
      </Header>
      <Divider />
      <Footer>
        <FooterCol>
          <MetaLabel>{t("externalReferenceLabel")}</MetaLabel>
          <FooterValue>{part.externalReference}</FooterValue>
        </FooterCol>
        {part.validUntil != null && (
          <FooterCol>
            <MetaLabel>{t("validUntilLabel")}</MetaLabel>
            <FooterValue>{formatDayMonthYear(part.validUntil, locale)}</FooterValue>
          </FooterCol>
        )}
      </Footer>
      {/* Temporarily disabled.
      <CtaButton type="button" disabled={!onOpen} onClick={() => onOpen?.(part.externalReference)}>
        {t("ctaButton")}
      </CtaButton>
      */}
    </Card>
  );
}
